import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { applyBranding } from "@/lib/branding";
import {
  LayoutDashboard,
  Users,
  UsersRound,
  Pill,
  FilePlus2,
  History,
  Settings,
  LogOut,
  Stethoscope,
  Boxes,
  Bell,
  Share2,
  UserCog,
  UserCircle,
  ShieldCheck,
  ClipboardList,
  Hospital,
  Receipt,
  BookOpen,
  CalendarDays,
  QrCode,
  Smartphone,
  Palette,
  Menu,
  X,
} from "lucide-react";
import { visibleNavFor, effectiveRolesOf } from "@/lib/nav";

function isStandalonePWA() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

export default function Layout() {
  const { user, logout, switchHospital } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState(null);
  // Seeded from the login payload so the sidebar is right on first paint, then
  // refreshed from /hospital/modules (both are the ACTIVE hospital's type).
  const [clinicType, setClinicType] = useState(user?.clinic_type || "medical");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const sync = () => {
      setIsMobile(window.innerWidth < 768 || isStandalonePWA());
    };
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get("/hospital/modules")
      .then(({ data }) => {
        if (cancelled) return;
        setModules(data?.modules || {});
        if (data?.clinic_type) setClinicType(data.clinic_type);
      })
      .catch(() => {
        if (!cancelled) setModules({});
      });
    return () => { cancelled = true; };
  }, [user?.active_hospital_id]);

  const role = user?.role || "staff";
  const effectiveRoles = Array.isArray(user?.roles) && user.roles.length ? user.roles : [role];
  const hospitalLabel = user?.active_hospital_name || user?.hospital_name || "eMedbay";
  const branding = user?.branding || {};
  const primary = branding.primary_color || "";
  const accent = branding.accent_color || "";

  // Apply CSS vars on mount + whenever branding changes
  useEffect(() => {
    applyBranding(branding);
  }, [primary, accent]);
  const memberships = Array.isArray(user?.memberships) ? user.memberships : [];
  const showSwitcher = memberships.length >= 2;

  // Shared with the PWA drawer (MobileShell) — this filter used to be inlined
  // here as a second copy, which is exactly how the two drift apart.
  const navItems = visibleNavFor({
    effectiveRoles: effectiveRolesOf(user),
    modules,
    moduleOverrides: user?.module_overrides || {},
    clinicType,
  });

  const handleSwitch = async (hid) => {
    if (!hid || hid === user?.active_hospital_id) return;
    try {
      await switchHospital(hid);
      toast.success(`Switched to ${memberships.find((m) => m.hospital_id === hid)?.hospital_name || "workspace"}`);
      // Go to "/" rather than reloading in place: the current path may not exist
      // in the new tenant (a dental route in a medical hospital 403s), and
      // RootRedirect knows where this user belongs.
      window.location.assign("/");
    } catch (e) {
      toast.error("Could not switch workspace");
    }
  };

  const handleNav = () => {
    if (isMobile) setMobileOpen(false);
  };

  const SidebarContent = (
    <>
      <div className="p-5 border-b border-slate-200 flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-xl text-white flex items-center justify-center overflow-hidden bg-blue-600"
          style={primary ? { backgroundColor: primary } : undefined}
          data-testid="sidebar-brand-logo"
        >
          {branding.logo_url ? (
            <img src={branding.logo_url} alt="" className="max-h-full max-w-full object-contain" />
          ) : (
            <Stethoscope size={21} />
          )}
        </div>
        <div className="min-w-0">
          <div className="font-bold text-slate-900 leading-none text-xl" style={{ fontFamily: "Work Sans" }}>
            {branding.logo_url ? hospitalLabel : "eMedbay"}
          </div>
          <div className="text-sm text-slate-600 mt-1 truncate" data-testid="sidebar-hospital-name" title={hospitalLabel}>
            {branding.logo_url ? "" : hospitalLabel}
          </div>
        </div>
      </div>

      {showSwitcher ? (
        <div className="px-4 py-4 border-b border-slate-200 bg-slate-50">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Active hospital</div>
          <Select value={user?.active_hospital_id || ""} onValueChange={handleSwitch}>
            <SelectTrigger className="h-11 text-sm bg-white" data-testid="hospital-switcher">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {memberships.map((m) => (
                <SelectItem key={m.hospital_id} value={m.hospital_id} data-testid={`switcher-option-${m.hospital_id}`}>
                  {m.hospital_name} <span className="text-[10px] text-slate-500 ml-1">({m.role})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={handleNav}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-base transition-colors ${
                  isActive
                    ? "bg-blue-50 text-blue-700 font-semibold"
                    : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <Icon size={20} />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-3 border-t border-slate-200">
        <div className="px-3 py-2 mb-2">
          <div className="text-sm font-semibold text-slate-900 truncate" data-testid="user-name">{user?.name || "User"}</div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          <div className="text-[10px] uppercase tracking-wider text-blue-700 mt-1">{user?.role}</div>
        </div>
        <Button variant="outline" size="sm" className="w-full justify-start h-11" onClick={logout} data-testid="btn-logout">
          <LogOut size={16} className="mr-2" /> Logout
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {isMobile ? (
        <>
          <header className="no-print sticky top-0 z-40 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="w-11 h-11 rounded-xl bg-slate-100 text-slate-800 flex items-center justify-center"
              aria-label="Open menu"
            >
              <Menu size={23} />
            </button>

            <div className="text-center min-w-0 px-2">
              <div className="text-lg font-black text-slate-900 leading-tight" style={{ fontFamily: "Work Sans" }}>
                eMedbay
              </div>
              <div className="text-[11px] text-slate-500 truncate max-w-[210px]">
                {hospitalLabel}
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate("/doctor-mobile")}
              className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center"
              aria-label="Doctor mobile"
            >
              <Smartphone size={21} />
            </button>
          </header>

          {mobileOpen ? (
            <div className="fixed inset-0 z-50">
              <div className="absolute inset-0 bg-slate-950/60" onClick={() => setMobileOpen(false)} />
              <aside className="absolute left-0 top-0 bottom-0 w-[86vw] max-w-[360px] bg-white shadow-2xl flex flex-col">
                <div className="absolute right-3 top-3 z-10">
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-700"
                  >
                    <X size={20} />
                  </button>
                </div>
                {SidebarContent}
              </aside>
            </div>
          ) : null}

          <main className="min-w-0">
            <Outlet />
          </main>
        </>
      ) : (
        <div className="min-h-screen flex">
          <aside className="app-aside no-print w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col" data-testid="app-sidebar">
            {SidebarContent}
          </aside>

          <main className="flex-1 min-w-0">
            <Outlet />
          </main>
        </div>
      )}
    </div>
  );
}
